/*
 * The OpenStreetMap half of the map seam: Leaflet, raster tiles, no key.
 *
 * Three things here are deliberate and easy to undo by accident.
 *
 * 1. The root keeps gmap-vue's `vue-map-container` class. ShowOnMapModal's
 *    scoped CSS and qa/cypress/.../4_geoPages.spec.cy.ts:120 both select on
 *    it, so keeping it means the provider swap is invisible to CSS and to the
 *    suite. `data-testid="marker-map"` is the selector new specs should use.
 *
 * 2. With no tile URL configured the map draws a generated grid instead of
 *    imagery, rather than falling back to tile.openstreetmap.org. The OSMF
 *    tile policy does not cover a commercial product and a "temporary"
 *    fallback is how you get blocked. A tile-less map still pans, zooms, shows
 *    markers and reports clicks, which is everything a test needs -- and
 *    `data-tiles` says which mode it is in, so a spec can assert that CI is
 *    not silently talking to a tile server.
 *
 * 3. Markers are `divIcon`s carrying inline SVG, not Leaflet's default image
 *    icon. Leaflet's default resolves its PNGs relative to the CSS file, which
 *    webpack's content-hashing breaks; the usual fix is a pile of
 *    `mergeOptions` require() plumbing. A divIcon has no image to lose.
 */
import { defineComponent, onBeforeUnmount, onMounted, ref, watch } from '@vue/composition-api';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_MAX_ZOOM, MAP_TILE_URL } from '@/config/map';
/*
 * A 256px neutral grid, as a data URI. Offline, deterministic, and visibly not
 * a real map, so nobody mistakes a tile-less CI run for a working basemap.
 */
const STUB_TILE = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256">
     <rect width="256" height="256" fill="#eef1f4"/>
     <g stroke="#dce1e6" stroke-width="1">
       <path d="M0 64h256M0 128h256M0 192h256M64 0v256M128 0v256M192 0v256"/>
     </g>
   </svg>`)}`;
const PIN = `
  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
    <path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 22 14 22s14-11.5 14-22c0-7.73-6.27-14-14-14z"
          fill="#1f6feb"/>
    <circle cx="14" cy="14" r="5" fill="#ffffff"/>
  </svg>`;
export default defineComponent({
    props: {
        center: {
            type: Object,
            required: true
        },
        zoom: {
            type: Number,
            default: 16
        },
        markers: {
            type: Array,
            default: () => []
        },
        fitToMarkers: {
            type: Boolean,
            default: false
        },
        selectedMarkerId: {
            type: Number,
            default: null
        }
    },
    setup(props, { emit }) {
        const container = ref(null);
        const popupContent = ref(null);
        const popupHost = ref(null);
        const hasTiles = Boolean(MAP_TILE_URL);
        let map = null;
        let resizeObserver = null;
        let resizeTimer = null;
        let popup = null;
        let closingOurselves = false;
        const layers = new Map();
        /*
         * Two shapes, one function. A marker with a `label` draws that text as a
         * pill, because the search map's whole point is showing a price per venue
         * and a price behind a hover is a different, worse feature. A marker
         * without one keeps the plain pin.
         *
         * The label is escaped rather than interpolated raw: it is a formatted
         * price today, but it reaches here as a string from a consumer, and a
         * divIcon takes HTML.
         */
        const escapeHtml = (value) => value.replace(/[&<>"']/g, character => {
            switch (character) {
                case '&':
                    return '&amp;';
                case '<':
                    return '&lt;';
                case '>':
                    return '&gt;';
                case '"':
                    return '&quot;';
                default:
                    return '&#39;';
            }
        });
        const icon = (marker) => {
            // Context, not content. The nearby panel draws one venue and up to a few
            // dozen restaurants and car parks around it; if they all wore the same
            // teardrop the venue would be lost in its own neighbourhood. A small dot
            // reads as "there is something here" without competing.
            if (marker.variant === 'poi') {
                return L.divIcon({
                    html: '<span class="leaflet-marker-map__dot"></span>',
                    className: 'leaflet-marker-map__poi',
                    iconSize: [14, 14],
                    iconAnchor: [7, 7],
                    popupAnchor: [0, -8]
                });
            }
            if (marker.label) {
                return L.divIcon({
                    html: `<span class="leaflet-marker-map__price">${escapeHtml(marker.label)}</span>`,
                    className: 'leaflet-marker-map__pin leaflet-marker-map__pin--label',
                    iconSize: [0, 0],
                    iconAnchor: [0, 0],
                    popupAnchor: [0, -14]
                });
            }
            return L.divIcon({
                html: PIN,
                className: 'leaflet-marker-map__pin',
                iconSize: [28, 36],
                iconAnchor: [14, 36],
                popupAnchor: [0, -34]
            });
        };
        const drawMarkers = () => {
            if (!map)
                return;
            layers.forEach(layer => layer.remove());
            layers.clear();
            props.markers.forEach(marker => {
                const layer = L.marker([marker.position.lat, marker.position.lng], {
                    icon: icon(marker),
                    title: marker.title,
                    zIndexOffset: marker.zIndex || 0,
                    keyboard: true,
                    alt: marker.title
                });
                layer.on('click', (event) => {
                    // Without this the click also reaches the map and immediately
                    // closes the popup the consumer is about to open.
                    L.DomEvent.stopPropagation(event.originalEvent);
                    emit('marker-click', marker);
                });
                layer.addTo(map);
                layers.set(marker.id, layer);
            });
            if (props.fitToMarkers && props.markers.length > 1) {
                map.fitBounds(L.latLngBounds(props.markers.map(marker => [marker.position.lat, marker.position.lng])), { padding: [40, 40] });
            }
        };
        /*
         * Move the popup body back under the host it came from.
         *
         * Idempotent and defensive on purpose: this runs from Leaflet's `remove`
         * event, which also fires during teardown, and a node that is already back
         * in the host must not be moved again.
         */
        const restorePopupContent = () => {
            const content = popupContent.value;
            const host = popupHost.value;
            if (!content || !host || content.parentElement === host)
                return;
            host.appendChild(content);
        };
        const syncPopup = () => {
            if (!map)
                return;
            const marker = props.markers.find(candidate => candidate.id === props.selectedMarkerId);
            if (!marker) {
                if (popup) {
                    // Closing on our own initiative must not be reported back as a user
                    // dismissal, or the consumer deselects twice.
                    closingOurselves = true;
                    map.closePopup(popup);
                    closingOurselves = false;
                    popup = null;
                }
                return;
            }
            popup = L.popup({ closeButton: true, autoPan: true })
                .setLatLng([marker.position.lat, marker.position.lng])
                // The slot's rendered DOM is moved into the popup rather than
                // stringified, so Vue keeps owning it and its listeners survive.
                .setContent(popupContent.value || marker.title)
                .openOn(map);
            popup.on('remove', () => {
                // PUT THE CONTENT BACK. `setContent(element)` does not copy, it
                // APPENDS -- Leaflet physically moves our <div ref="popupContent">
                // out of the host and into the popup. When the popup is then removed,
                // that div goes with it into a detached tree, and the ref still points
                // at it because Vue never re-rendered it. The next open therefore
                // handed Leaflet a node that is no longer in the document.
                //
                // Symptom, reported from the venue page: the first pin click shows a
                // card, and no click after that ever shows one again.
                restorePopupContent();
                if (!closingOurselves)
                    emit('map-click');
            });
        };
        onMounted(() => {
            // A centre with no coordinates means the consumer has no venue to show
            // yet. Leaflet throws on a NaN LatLng, so refuse rather than crash the
            // modal that contains us.
            if (!container.value ||
                !Number.isFinite(props.center?.lat) ||
                !Number.isFinite(props.center?.lng)) {
                return;
            }
            map = L.map(container.value, {
                center: [props.center.lat, props.center.lng],
                zoom: props.zoom,
                scrollWheelZoom: true,
                zoomControl: true,
                attributionControl: true
            });
            L.tileLayer(hasTiles ? MAP_TILE_URL : STUB_TILE, {
                maxZoom: MAP_TILE_MAX_ZOOM,
                attribution: hasTiles ? MAP_TILE_ATTRIBUTION : ''
            }).addTo(map);
            map.on('click', () => emit('map-click'));
            drawMarkers();
            syncPopup();
            // The modal animates in, so the container has no size when Leaflet
            // measures it. Without this the map renders one tile in the corner.
            window.setTimeout(() => map && map.invalidateSize(), 0);
            /*
             * Keep measuring, because one timeout is not enough.
             *
             * Leaflet caches the container size at init and only recomputes on
             * invalidateSize(). A single setTimeout(0) covers a container that is
             * merely animating, and nothing else. On mobile the search page mounts
             * this map inside a panel that is toggled on later ("Kaart verbergen" /
             * show map), so the container is genuinely zero-sized well past that
             * tick: the map then drew as a small square in the corner with the
             * markers piled outside the viewport and the rest of the panel grey.
             * Reported and reproduced on an S25, 2026-09-14.
             *
             * A ResizeObserver covers every case that a timeout guesses at: the
             * toggle, an orientation change, the browser chrome collapsing on
             * scroll, and the modal animation.
             */
            if (typeof ResizeObserver !== 'undefined') {
                resizeObserver = new ResizeObserver(() => {
                    if (!map)
                        return;
                    if (resizeTimer)
                        window.clearTimeout(resizeTimer);
                    // Coalesce: a toggle fires a burst of resize callbacks and each
                    // invalidateSize() is a full relayout.
                    resizeTimer = window.setTimeout(() => {
                        if (!map)
                            return;
                        map.invalidateSize();
                        // Re-fit after the size is known, or the bounds computed against
                        // the old, wrong viewport survive and the markers sit off screen.
                        if (props.fitToMarkers && props.markers.length > 1) {
                            map.fitBounds(L.latLngBounds(props.markers.map(marker => [
                                marker.position.lat,
                                marker.position.lng
                            ])), { padding: [40, 40] });
                        }
                    }, 120);
                });
                resizeObserver.observe(container.value);
            }
        });
        onBeforeUnmount(() => {
            if (resizeObserver) {
                resizeObserver.disconnect();
                resizeObserver = null;
            }
            if (resizeTimer) {
                window.clearTimeout(resizeTimer);
                resizeTimer = null;
            }
            if (map) {
                map.remove();
                map = null;
            }
            layers.clear();
        });
        watch(() => props.markers, drawMarkers);
        watch(() => props.selectedMarkerId, syncPopup);
        watch(() => props.center, center => map && map.setView([center.lat, center.lng], props.zoom));
        /*
         * Zoom needed its own watcher, and its absence was a real bug rather than
         * an omission for tidiness.
         *
         * The component accepted a `zoom` prop but only ever applied it as an
         * argument to the setView above, so a zoom change with no accompanying
         * centre change did nothing at all. That is exactly the shape of "In de
         * buurt": Search/Map.vue sets zoom to 16 on entering nearby mode, and the
         * map stayed where it was. Measured on the search page -- the tiles stayed
         * at z8 while the consumer believed it had asked for z16.
         *
         * setZoom rather than setView, so a zoom change does not also yank the
         * centre; the centre watcher above owns that.
         */
        watch(() => props.zoom, zoom => map && zoom && map.setZoom(zoom));
        /*
         * When fitting is switched OFF, re-assert the consumer's centre and zoom.
         *
         * Props do not arrive in one atomic update. Entering "In de buurt" changes
         * three at once -- zoom 8 to 16, centre to the venue, fitToMarkers true to
         * false -- and drawMarkers can still run once with the OLD fitToMarkers
         * and the OLD marker list, whose fitBounds over every venue in the country
         * then overwrites the zoom that was just set. Measured from the tile
         * requests: entering nearby mode asked for z16 and then immediately for
         * z8, which is the national bounds.
         *
         * Re-applying here makes the result independent of that ordering: the
         * moment fitting stops, what the consumer asked for is what is shown. The
         * alternative -- chasing the order the props happen to arrive in today --
         * is the kind of fix that holds until the next unrelated change.
         */
        watch(() => props.fitToMarkers, fit => {
            if (fit || !map || !props.center || !props.center.lat)
                return;
            map.setView([props.center.lat, props.center.lng], props.zoom);
        });
        return { container, hasTiles, popupContent, popupHost };
    }
});
